/**
 * cityDistricts.ts
 *
 * Static district data for all supported cities.
 * This is the single source of truth for which districts exist per city,
 * used by SuburbPickerStep and CitySuburbMap.
 *
 * District names are proper nouns (stored as-is as the `name` of a saved set's `district` area).
 * They are NOT translated — the same name is used in all UI languages.
 *
 * Centroids are approximate lat/lng used to place map markers.
 */

export interface District {
  name: string;
  lat: number;
  lng: number;
}

export interface CityDistricts {
  cityName: string;         // matches supported_cities.name (Polish)
  center: [number, number]; // map centre coordinate
  zoom: number;             // default zoom level
  districts: District[];
}

export const CITY_DISTRICTS: Record<string, CityDistricts> = {
  Warszawa: {
    cityName: 'Warszawa',
    center: [52.2297, 21.0122],
    zoom: 11,
    districts: [
      { name: 'Bemowo', lat: 52.2421, lng: 20.9126 },
      { name: 'Białołęka', lat: 52.3316, lng: 21.0206 },
      { name: 'Bielany', lat: 52.2947, lng: 20.9422 },
      { name: 'Mokotów', lat: 52.1937, lng: 21.0444 },
      { name: 'Ochota', lat: 52.2122, lng: 20.9726 },
      { name: 'Praga-Południe', lat: 52.2374, lng: 21.0713 },
      { name: 'Praga-Północ', lat: 52.2649, lng: 21.0273 },
      { name: 'Rembertów', lat: 52.2614, lng: 21.1628 },
      { name: 'Targówek', lat: 52.2752, lng: 21.0581 },
      { name: 'Ursus', lat: 52.1961, lng: 20.8829 },
      { name: 'Ursynów', lat: 52.141, lng: 21.0323 },
      { name: 'Wawer', lat: 52.1921, lng: 21.1974 },
      { name: 'Wesoła', lat: 52.2518, lng: 21.2293 },
      { name: 'Wilanów', lat: 52.1531, lng: 21.1104 },
      { name: 'Wola', lat: 52.2362, lng: 20.9548 },
      { name: 'Włochy', lat: 52.1861, lng: 20.9484 },
      { name: 'Śródmieście', lat: 52.2328, lng: 21.0191 },
      { name: 'Żoliborz', lat: 52.2676, lng: 20.9797 },
    ],
  },

  Kraków: {
    cityName: 'Kraków',
    center: [50.0647, 19.945],
    zoom: 12,
    districts: [
      { name: 'Bieńczyce', lat: 50.0861, lng: 20.0292 },
      { name: 'Bieżanów-Prokocim', lat: 50.0171, lng: 20.023 },
      { name: 'Bronowice', lat: 50.0833, lng: 19.8698 },
      { name: 'Czyżyny', lat: 50.0665, lng: 20.0088 },
      { name: 'Dębniki', lat: 50.0129, lng: 19.8507 },
      { name: 'Grzegórzki', lat: 50.065, lng: 19.9688 },
      { name: 'Krowodrza', lat: 50.0709, lng: 19.9166 },
      { name: 'Mistrzejowice', lat: 50.0974, lng: 20.0096 },
      { name: 'Nowa Huta', lat: 50.0806, lng: 20.1293 },
      { name: 'Podgórze', lat: 50.0431, lng: 19.953 },
      { name: 'Podgórze Duchackie', lat: 50.0162, lng: 19.9594 },
      { name: 'Prądnik Biały', lat: 50.1036, lng: 19.9536 },
      { name: 'Prądnik Czerwony', lat: 50.0859, lng: 19.9666 },
      { name: 'Stare Miasto', lat: 50.0614, lng: 19.9357 },
      { name: 'Swoszowice', lat: 49.9886, lng: 19.9541 },
      { name: 'Wzgórza Krzesławickie', lat: 50.0984, lng: 20.092 },
      { name: 'Zwierzyniec', lat: 50.0602, lng: 19.8643 },
      { name: 'Łagiewniki-Borek Fałęcki', lat: 50.02, lng: 19.9294 },
    ],
  },

  Wrocław: {
    cityName: 'Wrocław',
    center: [51.1079, 17.0385],
    zoom: 12,
    districts: [
      { name: 'Bieńkowice', lat: 51.0498, lng: 17.0917 },
      { name: 'Biskupin-Sępolno-Dąbie-Bartoszowice', lat: 51.1035, lng: 17.096 },
      { name: 'Borek', lat: 51.0819, lng: 17.0106 },
      { name: 'Brochów', lat: 51.0618, lng: 17.0813 },
      { name: 'Gaj', lat: 51.0776, lng: 17.0409 },
      { name: 'Gajowice', lat: 51.0964, lng: 17.0043 },
      { name: 'Grabiszyn-Grabiszynek', lat: 51.0896, lng: 16.9821 },
      { name: 'Gądów-Popowice Południowe', lat: 51.1239, lng: 16.9725 },
      { name: 'Huby', lat: 51.0895, lng: 17.0384 },
      { name: 'Jagodno', lat: 51.057, lng: 17.0581 },
      { name: 'Jerzmanowo-Jarnołtów-Strachowice-Osiniec', lat: 51.1149, lng: 16.8732 },
      { name: 'Karłowice-Różanka', lat: 51.1426, lng: 17.038 },
      { name: 'Klecina', lat: 51.062, lng: 16.9726 },
      { name: 'Kleczków', lat: 51.1284, lng: 17.0365 },
      { name: 'Kowale', lat: 51.1327, lng: 17.1051 },
      { name: 'Krzyki-Partynice', lat: 51.0652, lng: 17.0017 },
      { name: 'Księże', lat: 51.0795, lng: 17.0936 },
      { name: 'Kuźniki', lat: 51.1248, lng: 16.9482 },
      { name: 'Kłokoczyce', lat: 51.157, lng: 17.0968 },
      { name: 'Leśnica', lat: 51.1456, lng: 16.8703 },
      { name: 'Lipa Piotrowska', lat: 51.1773, lng: 16.9993 },
      { name: 'Maślice', lat: 51.1594, lng: 16.9336 },
      { name: 'Muchobór Mały', lat: 51.108, lng: 16.9688 },
      { name: 'Muchobór Wielki', lat: 51.0992, lng: 16.9421 },
      { name: 'Nadodrze', lat: 51.1204, lng: 17.0304 },
      { name: 'Nowe Żerniki', lat: 51.1364, lng: 16.9264 },
      { name: 'Nowy Dwór', lat: 51.1155, lng: 16.9549 },
      { name: 'Oporów', lat: 51.0782, lng: 16.9613 },
      { name: 'Osobowice-Rędzin', lat: 51.1662, lng: 16.9704 },
      { name: 'Ołbin', lat: 51.1235, lng: 17.0532 },
      { name: 'Ołtaszyn', lat: 51.0566, lng: 17.022 },
      { name: 'Pawłowice', lat: 51.1663, lng: 17.1068 },
      { name: 'Pilczyce-Kozanów-Popowice Północne', lat: 51.1397, lng: 16.9628 },
      { name: 'Plac Grunwaldzki', lat: 51.1118, lng: 17.0606 },
      { name: 'Polanowice-Poświętne-Ligota', lat: 51.1619, lng: 17.0404 },
      { name: 'Powstańców Śląskich', lat: 51.0932, lng: 17.0206 },
      { name: 'Pracze Odrzańskie', lat: 51.174, lng: 16.9099 },
      { name: 'Przedmieście Oławskie', lat: 51.0988, lng: 17.056 },
      { name: 'Przedmieście Świdnickie', lat: 51.104, lng: 17.0223 },
      { name: 'Psie Pole-Zawidawie', lat: 51.1518, lng: 17.1212 },
      { name: 'Sołtysowice', lat: 51.1524, lng: 17.0702 },
      { name: 'Stare Miasto', lat: 51.1107, lng: 17.0366 },
      { name: 'Swojczyce-Strachocin-Wojnów', lat: 51.105, lng: 17.1478 },
      { name: 'Szczepin', lat: 51.1158, lng: 17.0118 },
      { name: 'Tarnogaj', lat: 51.0802, lng: 17.0623 },
      { name: 'Widawa', lat: 51.1718, lng: 17.0138 },
      { name: 'Wojszyce', lat: 51.0629, lng: 17.0377 },
      { name: 'Zacisze-Zalesie-Szczytniki', lat: 51.1182, lng: 17.0872 },
      { name: 'Złotniki', lat: 51.1375, lng: 16.8895 },
      { name: 'Świniary', lat: 51.1968, lng: 16.9749 },
      { name: 'Żerniki', lat: 51.1213, lng: 16.9232 },
    ],
  },

  Gdańsk: {
    cityName: 'Gdańsk',
    center: [54.352, 18.6466],
    zoom: 12,
    districts: [
      { name: 'Aniołki', lat: 54.3665, lng: 18.6291 },
      { name: 'Brzeźno', lat: 54.4073, lng: 18.6373 },
      { name: 'Brętowo', lat: 54.3741, lng: 18.5677 },
      { name: 'Chełm', lat: 54.338, lng: 18.6187 },
      { name: 'Jasień', lat: 54.3401, lng: 18.56 },
      { name: 'Kokoszki', lat: 54.3548, lng: 18.4912 },
      { name: 'Krakowiec-Górki Zachodnie', lat: 54.358, lng: 18.7541 },
      { name: 'Letnica', lat: 54.3898, lng: 18.6471 },
      { name: 'Matarnia', lat: 54.3763, lng: 18.5084 },
      { name: 'Młyniska', lat: 54.3783, lng: 18.6331 },
      { name: 'Nowy Port', lat: 54.3999, lng: 18.6688 },
      { name: 'Oliwa', lat: 54.4088, lng: 18.5568 },
      { name: 'Olszynka', lat: 54.3366, lng: 18.6709 },
      { name: 'Orunia Górna-Gdańsk Południe', lat: 54.3129, lng: 18.6083 },
      { name: 'Orunia-Św. Wojciech-Lipce', lat: 54.3101, lng: 18.6292 },
      { name: 'Osowa', lat: 54.4301, lng: 18.4699 },
      { name: 'Piecki-Migowo', lat: 54.3578, lng: 18.5803 },
      { name: 'Przeróbka', lat: 54.3576, lng: 18.6836 },
      { name: 'Przymorze Małe', lat: 54.4092, lng: 18.5832 },
      { name: 'Przymorze Wielkie', lat: 54.4095, lng: 18.5983 },
      { name: 'Rudniki', lat: 54.35, lng: 18.6983 },
      { name: 'Siedlce', lat: 54.3487, lng: 18.6161 },
      { name: 'Stogi', lat: 54.3611, lng: 18.7128 },
      { name: 'Strzyża', lat: 54.3869, lng: 18.5829 },
      { name: 'Suchanino', lat: 54.3558, lng: 18.6072 },
      { name: 'Ujeścisko-Łostowice', lat: 54.331, lng: 18.5844 },
      { name: 'VII Dwór', lat: 54.3881, lng: 18.5712 },
      { name: 'Wrzeszcz Dolny', lat: 54.383, lng: 18.6151 },
      { name: 'Wrzeszcz Górny', lat: 54.3778, lng: 18.6073 },
      { name: 'Wyspa Sobieszewska', lat: 54.3411, lng: 18.8874 },
      { name: 'Wzgórze Mickiewicza', lat: 54.3434, lng: 18.6083 },
      { name: 'Zaspa-Młyniec', lat: 54.392, lng: 18.5974 },
      { name: 'Zaspa-Rozstaje', lat: 54.3989, lng: 18.6074 },
      { name: 'Gdańsk Główny', lat: 54.3570, lng: 18.6440 },
      { name: 'Żabianka-Wejhera-Jelitkowo-Tysiąclecia', lat: 54.4204, lng: 18.5831 },
    ],
  },

  Gdynia: {
    cityName: 'Gdynia',
    center: [54.5189, 18.5305],
    zoom: 12,
    districts: [
      { name: 'Babie Doły', lat: 54.5814, lng: 18.5394 },
      { name: 'Chwarzno-Wiczlino', lat: 54.4789, lng: 18.4405 },
      { name: 'Chylonia', lat: 54.5415, lng: 18.4632 },
      { name: 'Cisowa', lat: 54.5523, lng: 18.4343 },
      { name: 'Działki Leśne', lat: 54.5239, lng: 18.5405 },
      { name: 'Dąbrowa', lat: 54.4739, lng: 18.4658 },
      { name: 'Grabówek', lat: 54.5139, lng: 18.5405 },
      { name: 'Kamienna Góra', lat: 54.5289, lng: 18.5105 },
      { name: 'Karwiny', lat: 54.4734, lng: 18.4906 },
      { name: 'Leszczynki', lat: 54.5322, lng: 18.4869 },
      { name: 'Mały Kack', lat: 54.4808, lng: 18.5301 },
      { name: 'Obłuże', lat: 54.5522, lng: 18.5086 },
      { name: 'Oksywie', lat: 54.5589, lng: 18.5505 },
      { name: 'Orłowo', lat: 54.4794, lng: 18.5528 },
      { name: 'Pogórze', lat: 54.558, lng: 18.4813 },
      { name: 'Pustki Cisowskie-Demptowo', lat: 54.5301, lng: 18.4369 },
      { name: 'Redłowo', lat: 54.4989, lng: 18.5105 },
      { name: 'Wielki Kack', lat: 54.4677, lng: 18.4946 },
      { name: 'Witomino-Leśniczówka', lat: 54.4984, lng: 18.5092 },
      { name: 'Wzgórze Św. Maksymiliana', lat: 54.503, lng: 18.5433 },
      { name: 'Śródmieście', lat: 54.5197, lng: 18.5392 },
    ],
  },

  Sopot: {
    cityName: 'Sopot',
    center: [54.4418, 18.5601],
    zoom: 13,
    districts: [
      { name: 'Sopot', lat: 54.4418, lng: 18.5601 },
    ],
  },

};

/**
 * Cities open for tenant onboarding in v1. The Miasto onboarding step only
 * offers these; users wanting any other city go to the waiting list.
 * Widen this list (and the Miasto step UI) when new cities launch.
 */
export const LAUNCH_CITIES = ['Warszawa'];

/**
 * The 10 most-demanded Warsaw districts (DECIDED from seeker data 2026-07-23,
 * docs/OnboardingRevamp.md §3 N2) — shown in THIS order as the collapsed
 * quick-pick row on the Miasto onboarding step. The full alphabetical grid +
 * map sit behind the "Pokaż wszystkie dzielnice i mapę" expander.
 */
export const TOP_DISTRICTS = [
  'Mokotów',
  'Wola',
  'Śródmieście',
  'Ochota',
  'Żoliborz',
  'Praga-Południe',
  'Ursynów',
  'Bemowo',
  'Bielany',
  'Włochy',
];

/** The Trójmiasto metro area groups these three cities into one combined tab. */
export const TROJMIASTO_CITIES = ['Gdańsk', 'Sopot', 'Gdynia'] as const;

/** Combined map centre and zoom for the merged Trójmiasto tab. */
export const TROJMIASTO_VIEW = {
  center: [54.414, 18.576] as [number, number],
  zoom: 11,
};